import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";
import RichTextEditor from "components/form/RichTextEditor";

const BASE_URL = "https://api.kennapartners.com/api/v1"; // Base URL for all API calls

const Books = () => {
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isInitialLoadRef = useRef(true); // Track if this is initial load
  const isSearchingRef = useRef(false); // Track if we're currently searching
  const allBooksCacheRef = useRef(null); // Cache all books to avoid repeated API calls

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState("");
  const [newBookId, setNewBookId] = useState(null);

  // Current book being operated on
  const [currentBook, setCurrentBook] = useState(null);
  const [selectedBookId, setSelectedBookId] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: "",
    introduction: { html: "", text: "" },
    preface: { html: "", text: "" },
    foreword: { html: "", text: "" },
    isbn: "",
    date: "",
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    fetchBooks();
  }, [currentPage]); // Re-fetch books when page changes

  // Initialize filteredBooks ONLY on initial load
  useEffect(() => {
    if (books.length > 0 && isInitialLoadRef.current && !isSearchingRef.current) {
      applyFilters(books);
      isInitialLoadRef.current = false; // Mark that initial load is done
    }
  }, [books]);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim()) {
      isSearchingRef.current = true;
      const debounceTimer = setTimeout(() => {
        searchAllBooks(searchTerm.trim());
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      // Search cleared - reset to current page with status filter applied
      isSearchingRef.current = false;
      applyFilters(books);
    }
  }, [searchTerm]);

  // Status filter changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      applyFilters(books);
    } else {
      // Re-apply search with new status filter
      searchAllBooks(searchTerm.trim());
    }
  }, [statusFilter]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${BASE_URL}/books/?page=${currentPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data && data.data && Array.isArray(data.data.book)) {
        setBooks(data.data.book);
        setFilteredBooks(data.data.book); // Initialize filtered books
        setTotalItems(data.data.total_books || 0);
      } else {
        console.error(
          'Invalid data format. Expected an array of books under "data.book".'
        );
        setBooks([]);
        setFilteredBooks([]);
        setTotalItems(0);
      }
      setError("");
    } catch (error) {
      console.error("Error fetching books:", error);
      setError("Failed to fetch books. Please try again.");
      setBooks([]);
      setFilteredBooks([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const searchAllBooks = async (term) => {
    console.log("🔍 SEARCH TRIGGERED with term:", term);
    setLoading(true);
    try {
      let allBooks = allBooksCacheRef.current;

      // Only fetch if we don't have cached data
      if (!allBooks) {
        console.log("📡 Fetching all books from API...");
        const token = localStorage.getItem("access_token");
        
        // Fetch ALL books by looping through pages
        allBooks = [];
        let page = 1;
        let totalBooks = 0;

        // First, get the total count
        const firstResponse = await fetch(
          `${BASE_URL}/books/?page=1&limit=10`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!firstResponse.ok) throw new Error(`HTTP error! status: ${firstResponse.status}`);

        const firstData = await firstResponse.json();
        totalBooks = firstData.data?.total_books || 0;
        console.log(`📊 Total books in system: ${totalBooks}`);
        
        const firstPageBooks = firstData.data?.book || [];
        allBooks = [...firstPageBooks];
        console.log(`📄 Fetched page 1: ${firstPageBooks.length} books`);

        // Calculate total pages needed
        const totalPages = Math.ceil(totalBooks / 10);
        console.log(`📑 Total pages to fetch: ${totalPages}`);

        // Fetch remaining pages
        for (let p = 2; p <= totalPages; p++) {
          const response = await fetch(
            `${BASE_URL}/books/?page=${p}&limit=10`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            }
          );

          if (!response.ok) {
            console.warn(`Failed to fetch page ${p}`);
            continue;
          }

          const data = await response.json();
          const fetchedBooks = data.data?.book || [];
          
          console.log(`📄 Fetched page ${p}: ${fetchedBooks.length} books`);
          
          allBooks = [...allBooks, ...fetchedBooks];
        }

        console.log("📦 Total books fetched from all pages:", allBooks.length);
        if (allBooks.length > 0) {
          console.log("📝 First book sample:", allBooks[0]);
        }
        
        // Cache the results for future searches
        allBooksCacheRef.current = allBooks;
      } else {
        console.log("⚡ Using cached data:", allBooks.length, "books");
      }

      // Filter from cache - this is instant!
      let filtered = allBooks.filter((book) => {
        const nameMatch = book?.name?.toLowerCase().startsWith(term.toLowerCase());
        
        if (nameMatch) {
          console.log("✅ MATCH FOUND:", book?.name);
        }
        
        return nameMatch; // ONLY return names that START WITH the search term
      });

      console.log("🎯 Filtered results count:", filtered.length);

      // Apply status filter if needed
      if (statusFilter !== "all") {
        filtered = filtered.filter((book) => book.status === statusFilter);
        console.log("📋 After status filter:", filtered.length);
      }

      console.log("✅ Setting filteredBooks with", filtered.length, "items");
      setFilteredBooks(filtered);
    } catch (error) {
      console.error("❌ Error searching books:", error);
      setFilteredBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (items) => {
    let filtered = Array.isArray(items) ? items : [];

    if (statusFilter !== "all") {
      filtered = filtered.filter((book) => book.status === statusFilter);
    }

    setFilteredBooks(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      introduction: { html: "", text: "" },
      preface: { html: "", text: "" },
      foreword: { html: "", text: "" },
      isbn: "",
      date: "",
    });
    setFile(null);
    setNewBookId(null);
  };

  const handleCreateBook = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");

      // Prepare the payload with rich text content
      const payload = {
        name: formData.name,
        introduction: formData.introduction,
        preface: formData.preface,
        foreword: formData.foreword,
        isbn: formData.isbn,
        date: formData.date,
      };

      const response = await fetch(`${BASE_URL}/books/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newBook = data.data.book;

      if (!newBook || !newBook.id) {
        throw new Error("New book ID is undefined");
      }

      setBooks((prev) => [...prev, newBook]);
      setNewBookId(newBook.id);

      if (file) {
        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse = await fetch(
          `${BASE_URL}/books/${newBook.id}/upload`,
          {
            method: "PATCH",
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: formData,
          }
        );

        if (!uploadResponse.ok) {
          throw new Error(
            `File upload failed! status: ${uploadResponse.status}`
          );
        }

        const updatedBook = await uploadResponse.json();
        setBooks((prev) =>
          prev.map((book) => (book.id === updatedBook.id ? updatedBook : book))
        );
      }

      setShowCreateModal(false);
      resetForm();
      setFile(null);
      setError("");
      fetchBooks(); // Refresh the list
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "Failed to create book. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookDetails = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/books/${id}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const bookData = data.book || data.data?.book || data;

      if (!bookData) {
        throw new Error("No book data found in response");
      }

      setCurrentBook(bookData);
      setError("");
    } catch (error) {
      console.error("Error fetching book details:", error);
      setError("Failed to fetch book details.");
      setCurrentBook(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBook = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");

      // Prepare the payload with rich text content
      const payload = {
        name: formData.name,
        introduction: formData.introduction,
        preface: formData.preface,
        foreword: formData.foreword,
        isbn: formData.isbn,
        date: formData.date,
      };

      const response = await fetch(`${BASE_URL}/books/${currentBook.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedBook = await response.json();
      setBooks((prev) =>
        prev.map((book) => (book.id === updatedBook.id ? updatedBook : book))
      );
      setShowEditModal(false);
      setCurrentBook(null);
      resetForm();
      setError("");
      fetchBooks(); // Refresh the list
    } catch (error)
    {
      console.error("Error updating book:", error);
      setError("Failed to update book. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBook = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/books/${selectedBookId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setBooks((prev) => prev.filter((book) => book.id !== selectedBookId));
      setShowDeleteModal(false);
      setSelectedBookId(null);
      setError("");
      // Reset to page 1 if we deleted the last item on the current page
      if (filteredBooks.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchBooks(); // Refresh current page if not moving back
      }
    } catch (error) {
      console.error("Error deleting book:", error);
      setError("Failed to delete book. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const fileInput = e.target.file;
    if (!fileInput.files[0]) {
      setError("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", fileInput.files[0]);

    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/books/${selectedBookId}/upload`,
        {
          method: "PATCH",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedBook = await response.json();
      setBooks((prev) =>
        prev.map((book) => (book.id === updatedBook.id ? updatedBook : book))
      );
      setShowUploadModal(false);
      setSelectedBookId(null);
      setError("");
    } catch (error) {
      console.error("Error uploading file:", error);
      setError("Failed to upload file. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (book) => {
    setCurrentBook(book);
    setFormData({
      name: book.name || "",
      introduction:
        typeof book.introduction === "object"
          ? book.introduction
          : { html: book.introduction || "", text: "" },
      preface:
        typeof book.preface === "object"
          ? book.preface
          : { html: book.preface || "", text: "" },
      foreword:
        typeof book.foreword === "object"
          ? book.foreword
          : { html: book.foreword || "", text: "" },
      isbn: book.isbn || "",
      date: book.date || "",
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (bookId) => {
    setSelectedBookId(bookId);
    setShowDeleteModal(true);
  };

  const openDetailsModal = async (bookId) => {
    setSelectedBookId(bookId);
    await fetchBookDetails(bookId);
    setShowDetailsModal(true);
  };

  const openUploadModal = (bookId) => {
    setSelectedBookId(bookId);
    setShowUploadModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setShowUploadModal(false);
    setCurrentBook(null);
    setSelectedBookId(null);
    resetForm();
  };

  /* ------------------------ PAGINATION ------------------------ */
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex justify-between items-center mt-4 px-4 pb-4">
        <div>
          <p className="text-sm ">
            Showing{" "}
            <span className="font-medium">
              {(currentPage - 1) * itemsPerPage + 1}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> books
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page
                  ? ""
                  : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="workspace">
      {fileSizeError && (
        <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",zIndex:9999,display:"flex",alignItems:"flex-start",gap:"12px",backgroundColor:"#dc2626",color:"white",fontSize:"14px",fontWeight:"500",padding:"16px 20px",borderRadius:"10px",boxShadow:"0 10px 40px rgba(0,0,0,0.35)",maxWidth:"500px",width:"calc(100% - 32px)"}}>
          <span style={{fontSize:"20px",lineHeight:"1",flexShrink:0}}>🚫</span>
          <div style={{flex:1,lineHeight:"1.5"}}>{fileSizeError}</div>
          <button onClick={()=>setFileSizeError("")} style={{marginLeft:"8px",background:"none",border:"none",color:"white",fontSize:"22px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      )}
      <Breadcrumb title="Books">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Books</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button
            className="float-right text-white"
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Books Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New Book
          </button>
          <button
            onClick={fetchBooks}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="Search books by title or ISBN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-control"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-control"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !books.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading books...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                  <thead>
                    <tr>
                      <th className="w-1/3">Title</th>
                      <th className="w-1/6">Date</th>
                      <th className="w-1/6">File URL</th>
                      <th className="w-1/3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(filteredBooks) &&
                    filteredBooks.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8">
                          {books.length === 0
                            ? "No books found. Create your first book!"
                            : "No books match your search criteria."}
                        </td>
                      </tr>
                    ) : (
                      filteredBooks.map((book) => (
                        <tr key={book.id}>
                          <td className="w-1/3">
                            <div className="font-medium">{book.name}</div>
                          </td>
                          <td className="w-1/6">
                            {book.date
                              ? new Date(book.date).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td className="w-1/6">
                            <a
                              href={book.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              View File
                            </a>
                          </td>
                          <td className="w-1/3">
                            <div className="flex gap-1">
                              <Button
                                onClick={() => openDetailsModal(book.id)}
                                className="btn btn-sm btn-info"
                                title="View Details"
                              >
                                <span className="la la-eye"></span>
                              </Button>
                              <Button
                                onClick={() => openEditModal(book)}
                                className="btn btn-sm btn-warning"
                                title="Edit Book"
                              >
                                <span className="la la-edit"></span>
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => openDeleteModal(book.id)}
                                className="btn btn-sm btn-danger"
                                title="Delete Book"
                              >
                                <span className="la la-trash"></span>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* CREATE MODAL */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-4 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold">
                    Create New Book
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <form onSubmit={handleCreateBook}>
                  <div className="modal-body p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          ISBN (optional)
                        </label>
                        <Input
                          type="text"
                          name="isbn"
                          value={formData.isbn}
                          onChange={handleInputChange}
                          placeholder="e.g. 978-0-321-76572-6"
                        />
                      </div>
                      <div className="form-group col-span-2">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Introduction
                        </label>
                        <RichTextEditor
                          value={formData.introduction.html}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              introduction: {
                                html,
                                text: html.replace(/<[^>]*>/g, ""),
                              },
                            }))
                          }
                          uploadEndpoint={`${BASE_URL}/books/editor/upload`}
                        />
                      </div>
                      <div className="form-group col-span-2">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Preface
                        </label>
                        <RichTextEditor
                          value={formData.preface.html}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              preface: {
                                html,
                                text: html.replace(/<[^>]*>/g, ""),
                              },
                            }))
                          }
                          uploadEndpoint={`${BASE_URL}/books/editor/upload`}
                        />
                      </div>
                      <div className="form-group col-span-2">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Foreword
                        </label>
                        <RichTextEditor
                          value={formData.foreword.html}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              foreword: {
                                html,
                                text: html.replace(/<[^>]*>/g, ""),
                              },
                            }))
                          }
                          uploadEndpoint={`${BASE_URL}/books/editor/upload`}
                        />
                      </div>
                      <div className="form-group">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Date
                        </label>
                        <Input
                          type="datetime-local"
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="form-group col-span-2">
                      <label className="block mb-2 text-sm font-medium text-gray-900">
                        Upload File
                      </label>
                      <input
                        type="file"
                        onChange={(e) => { const f=e.target.files[0]; if(!f) return; if(f.size > 2097152){ setFileSizeError(`⚠️ "${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`); e.target.value=""; return; } setFileSizeError(""); setFile(f); }}
                        className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
                        accept=".png, .jpeg"
                      />
                      <small className="form-text text-muted">
                        Supported formats: PNG, JPEG
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer flex items-center justify-center p-6 border-t border-gray-200 rounded-b gap-4">
                    <Button
                      type="submit"
                      className="text-white bg-blue-500 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                      disabled={loading}
                    >
                      {loading ? "Creating..." : "Submit"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-4 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold">
                    Edit Book
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <form onSubmit={handleUpdateBook}>
                  <div className="modal-body p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          ISBN
                        </label>
                        <Input
                          type="text"
                          name="isbn"
                          value={formData.isbn}
                          onChange={handleInputChange}
                          placeholder="e.g. 978-0-321-76572-6"
                        />
                      </div>
                      <div className="form-group">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Date
                        </label>
                        <Input
                          type="datetime-local"
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-group col-span-2">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Introduction
                        </label>
                        <RichTextEditor
                          value={formData.introduction.html}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              introduction: {
                                html,
                                text: html.replace(/<[^>]*>/g, ""),
                              },
                            }))
                          }
                          uploadEndpoint={`${BASE_URL}/books/editor/upload`}
                        />
                      </div>
                      <div className="form-group col-span-2">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Preface
                        </label>
                        <RichTextEditor
                          value={formData.preface.html}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              preface: {
                                html,
                                text: html.replace(/<[^>]*>/g, ""),
                              },
                            }))
                          }
                          uploadEndpoint={`${BASE_URL}/books/editor/upload`}
                        />
                      </div>
                      <div className="form-group col-span-2">
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Foreword
                        </label>
                        <RichTextEditor
                          value={formData.foreword.html}
                          onChange={(html) =>
                            setFormData((prev) => ({
                              ...prev,
                              foreword: {
                                html,
                                text: html.replace(/<[^>]*>/g, ""),
                              },
                            }))
                          }
                          uploadEndpoint={`${BASE_URL}/books/editor/upload`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer flex items-center justify-center p-6 border-t border-gray-200 rounded-b gap-4">
                    <Button
                      type="button"
                      onClick={closeAllModals}
                      className="text-white bg-red-500 hover:bg-red-700 px-6"
                    >
                      Close
                    </Button>
                    <Button
                      type="submit"
                      className="text-white bg-blue-500 hover:bg-blue-700 px-6"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Book"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-4xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-4 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold">
                    Book Details
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>
                <div className="modal-body p-6">
                  {loading ? (
                    <div className="text-center py-4">
                      <span className="la la-spinner la-spin text-2xl"></span>
                      <p>Loading details...</p>
                    </div>
                  ) : currentBook ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Basic Information
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Name
                              </label>
                              <p className="mt-1 text-sm text-gray-900 font-semibold">
                                {currentBook.name || "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                ISBN
                              </label>
                              <p>{currentBook.isbn || "Not specified"}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Publication Date
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {currentBook.date
                                  ? new Date(
                                      currentBook.date
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Created At
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {currentBook.created_at
                                  ? new Date(
                                      currentBook.created_at
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500">
                                Last Updated
                              </label>
                              <p className="mt-1 text-sm text-gray-900">
                                {currentBook.updated_at
                                  ? new Date(
                                      currentBook.updated_at
                                    ).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "Not specified"}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            File Information
                          </h3>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">
                              File URL
                            </label>
                            <div className="mt-1">
                              {currentBook.file_url ? (
                                <a
                                  href={currentBook.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm break-all"
                                >
                                  {currentBook.file_url}
                                </a>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  No file uploaded
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Introduction
                          </h3>
                          <div className="prose prose-sm max-w-none">
                            {currentBook.introduction?.html ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: currentBook.introduction.html,
                                }}
                              />
                            ) : (
                              <p className="text-gray-700 whitespace-pre-line">
                                {currentBook.introduction ||
                                  "No introduction available."}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Preface
                          </h3>
                          <div className="prose prose-sm max-w-none">
                            {currentBook.preface?.html ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: currentBook.preface.html,
                                }}
                              />
                            ) : (
                              <p className="text-gray-700 whitespace-pre-line">
                                {currentBook.preface || "No preface available."}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Foreword
                          </h3>
                          <div className="prose prose-sm max-w-none">
                            {currentBook.foreword?.html ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: currentBook.foreword.html,
                                }}
                              />
                            ) : (
                              <p className="text-gray-700 whitespace-pre-line">
                                {currentBook.foreword ||
                                  "No foreword available."}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-red-500 mb-4">
                        Failed to load book details.
                      </p>
                      <button
                        onClick={() => fetchBookDetails(selectedBookId)}
                        className="btn bg-blue-500 text-white px-4 py-2 rounded"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                </div>
                <div className="modal-footer flex justify-end p-4 border-t gap-4">
                  <Button
                    onClick={closeAllModals}
                    className="btn bg-gray-300 text-gray-700 hover:bg-gray-400 px-6"
                  >
                    Close
                  </Button>
                  {currentBook && (
                    <Button
                      onClick={() => {
                        closeAllModals();
                        openEditModal(currentBook);
                      }}
                      className="btn bg-blue-600 text-white hover:bg-blue-700 px-6"
                    >
                      Edit Book
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete this book? This action
                    cannot be undone.
                  </p>
                </div>
                <div className="modal-footer flex justify-center gap-4">
                  <button
                    onClick={closeAllModals}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                  >
                    Cancel
                  </button>
                  <Button
                    color="danger"
                    onClick={handleDeleteBook}
                    className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    disabled={loading}
                  >
                    {loading ? "Deleting..." : "Delete Book"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Upload File</h5>
                  <button
                    onClick={closeAllModals}
                    className="btn-close text-red-500 dark:text-red-500 hover:text-red-700"
                  ></button>
                </div>
                <form onSubmit={handleFileUpload}>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>Select File</label>
                      <input
                        type="file"
                        name="file"
                        className="form-control"
                        accept=".pdf,.doc,.docx,.epub,.txt"
                        required
                      />
                      <small className="form-text text-muted">
                        Supported formats: PDF, DOC, DOCX, EPUB, TXT
                      </small>
                    </div>
                  </div>
                  <div className="modal-footer flex justify-center gap-4">
                    <button
                      type="button"
                      onClick={closeAllModals}
                      className="btn bg-red-500 text-white hover:bg-red-700 px-6"
                    >
                      Cancel
                    </button>
                    <Button
                      type="submit"
                      className="btn bg-blue-500 text-white hover:bg-blue-700 px-6"
                      disabled={loading}
                    >
                      {loading ? "Uploading..." : "Upload File"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
};

export default Books;